import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(identifier, password, "admin");
      navigate("/admin");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B192C] px-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      data-testid="admin-login-page">
      <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full opacity-25 blur-3xl" style={{ background: "#2563EB" }} />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full opacity-20 blur-3xl" style={{ background: "#10B981" }} />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <div className="mb-6 text-center">
          <img src="/logo.png" alt="DropPartner" className="mx-auto h-20 w-auto rounded-2xl bg-white object-contain p-2 shadow-xl" />
          <div className="mt-5 flex items-center justify-center gap-2">
            <ShieldCheck size={16} className="text-emerald-400" />
            <h1 className="text-xl font-extrabold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Admin Control Center</h1>
          </div>
          <p className="mt-1 text-xs text-slate-400">Restricted access — DropPartner operations team only</p>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-[#1B2C44] bg-[#101F33] p-6 shadow-2xl" data-testid="admin-login-form">
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Admin Email</label>
            <Input data-testid="admin-login-email" type="email" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              placeholder="admin@droppartner.in" autoComplete="username"
              style={{ color: "#F1F5F9", caretColor: "#10B981", backgroundColor: "#0B192C" }}
              className="border-[#22385A] placeholder:text-slate-500 focus-visible:ring-emerald-500" required />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Password</label>
            <div className="relative">
              <Input data-testid="admin-login-password" type={show ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password"
                style={{ color: "#F1F5F9", caretColor: "#10B981", backgroundColor: "#0B192C" }}
                className="border-[#22385A] pr-10 placeholder:text-slate-500 focus-visible:ring-emerald-500" required />
              <button type="button" data-testid="admin-toggle-password" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          {error && <p data-testid="admin-login-error" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>}
          <Button data-testid="admin-login-submit" disabled={busy}
            className="w-full bg-emerald-500 font-bold text-[#0B192C] shadow-lg transition-all hover:bg-emerald-400">
            {busy ? "Verifying…" : "Sign in to Admin"}
          </Button>
          <p className="text-center text-[11px] text-slate-500">Partner? <Link to="/login" data-testid="admin-to-partner-login" className="font-semibold text-sky-400 hover:text-sky-300">Use the partner login</Link></p>
        </form>
        <p className="mt-5 text-center text-[10px] text-slate-600">All admin sign-ins are logged and rate-limited.</p>
      </motion.div>
      <style>{`
        [data-testid="admin-login-page"] input { color: #F1F5F9 !important; caret-color: #10B981; -webkit-text-fill-color: #F1F5F9 !important; }
        [data-testid="admin-login-page"] input:-webkit-autofill,
        [data-testid="admin-login-page"] input:-webkit-autofill:hover,
        [data-testid="admin-login-page"] input:-webkit-autofill:focus {
          -webkit-text-fill-color: #F1F5F9 !important;
          -webkit-box-shadow: 0 0 0 60px #0B192C inset !important;
          caret-color: #10B981;
        }
      `}</style>
    </div>
  );
}
